import { Icon, Text, useIsBreakpointLargeAndUp } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  useAppRouter,
  useCountry,
  useOfferDetailPBI,
  usePBIRejected,
  useAppropriatePaymentMethod,
  usePaymentOptionsError,
  selectPurchaseMethod,
  setCheckoutStatus,
  useAppDispatch,
  usePaymentOptions,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { usePBIRejectedText } from './helper'
import styles from './style.module.css'
import { Country } from '@afterpay/types'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { CheckoutStatus } from '../../model'

// PaymentTypes Components
import { ConsumerWelcome } from './ConsumerWelcome'
import { PaymentTypesPBIRejected } from './PaymentTypesPBIRejected'
import { PaymentTypesRadioGroup } from './PaymentTypesRadioGroup'
import { PaymentTypesTerms } from './PaymentTypesTerms'
import { PaymentTypesContinueButton } from './PaymentTypesContinueButton'

const PBIRejectedRadioMessage = (): JSX.Element => {
  const { smallTextSize } = useCheckoutSizes()
  const rejectedTextPBI = usePBIRejectedText()

  return (
    <div className={styles.PBIDeclineWrapper} data-testid='pbi-decline-wrapper'>
      <span className={styles.rejectedInfoIcon}>
        <Icon.Info size='ExtraSmall' />
      </span>
      <span className={styles.rejectedText}>
        <Text size={smallTextSize}>{rejectedTextPBI}</Text>
      </span>
    </div>
  )
}

export const PaymentTypes: FunctionComponent = () => {
  const { t } = useTranslation()
  const isDesktop = useIsBreakpointLargeAndUp()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const hasPBIRejected = usePBIRejected()
  const offerDetailPBI = useOfferDetailPBI()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()
  const paymentMethod = useAppropriatePaymentMethod()
  const paymentOptionsError = usePaymentOptionsError()
  const paymentOptions = usePaymentOptions()

  useEffect(() => {
    // Don't allow user to select new payment option from this page
    // if payment options have already been populated
    if (paymentOptions?.length > 0) {
      router.push(Route.PAYMENT_OPTIONS)
    }
  }, [])
  const [pbiOptionSelected, setPbiOptionSelected] = useState<boolean>(false)

  useEffect(() => {
    if (hasPBIRejected) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: offerDetailPBI?.rejectedReasonCode })
    }
  }, [])

  useEffect(() => {
    router.prefetch(Route.PAYMENT_OPTIONS)
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.SUMMARY)
  }, [])

  useEffect(() => {
    if (pbiOptionSelected || paymentOptionsError !== undefined) {
      if (paymentMethod !== null) {
        dispatch(selectPurchaseMethod(paymentMethod, false))
      } else {
        dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
        router.push(Route.ADD_PAYMENT_METHOD)
        return
      }
      router.push(Route.SUMMARY)
    }
  }, [pbiOptionSelected, paymentOptionsError])

  const showPBIRejectedView = hasPBIRejected && country === Country.US

  return (
    <>
      {showPBIRejectedView && <PaymentTypesPBIRejected />}
      {!showPBIRejectedView && (
        <>
          <ConsumerWelcome />
          <div className={isDesktop ? styles.paymentTypesWrapperDesktop : styles.paymentTypesWrapperMobile}>
            <PaymentTypesRadioGroup />
            {hasPBIRejected && <PBIRejectedRadioMessage />}
            <PaymentTypesTerms />
            <PaymentTypesContinueButton setPbiOptionSelected={setPbiOptionSelected} />
          </div>
        </>
      )}
    </>
  )
}
